import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface ConfirmModalProps {
    icon: ReactNode;
    iconClassName?: string;
    title: string;
    message: ReactNode;
    confirmLabel: string;
    confirmClassName?: string;
    isLoading?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

export function ConfirmModal({
    icon,
    iconClassName,
    title,
    message,
    confirmLabel,
    confirmClassName,
    isLoading,
    onConfirm,
    onCancel,
}: ConfirmModalProps) {
    const { t } = useTranslation();

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px] animate-in fade-in duration-200"
                onClick={onCancel}
            />

            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm relative z-10 p-6 animate-in zoom-in-95 duration-200 border border-slate-100">
                <div className="flex flex-col items-center text-center gap-4">
                    <div className={cn("w-12 h-12 rounded-full flex items-center justify-center mb-2", iconClassName)}>
                        {icon}
                    </div>

                    <div>
                        <h3 className="text-lg font-bold text-slate-900">{title}</h3>
                        <p className="text-sm text-slate-500 mt-2">{message}</p>
                    </div>

                    <div className="flex gap-3 w-full mt-2">
                        <button
                            onClick={onCancel}
                            className="flex-1 py-3 px-4 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors active:scale-95"
                        >
                            {t('btn.cancel')}
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isLoading}
                            className={cn(
                                "flex-1 py-3 px-4 rounded-xl font-bold text-white transition-colors shadow-lg active:scale-95 flex items-center justify-center gap-2",
                                confirmClassName ?? "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200",
                            )}
                        >
                            {isLoading ? <Loader2 size={18} className="animate-spin" /> : confirmLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
